import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import dayjs from "dayjs";

import {
  AppointmentsService,
  CustomerAppointmentUpdateRequest,
} from "../../../../shared/services/user-appointments.service";

import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
// Adjust the two shared import paths above only if your
// actual service/constants folders differ.

interface CatalogService {
  service_id: string;
  name: string;
  price: string | number;
  duration_minutes: number;
}

interface CatalogStaff {
  staff_id: string;
  full_name: string;
  email?: string;
  job_title?: string;
}

interface SalonCatalog {
  salon_id: string;
  name: string;
  services: CatalogService[];
  staff: CatalogStaff[];
}

interface AvailabilitySlot {
  time: string;
  available: boolean;
}

interface AvailabilityResponse {
  date: string;
  staff_id: string;
  duration_minutes: number;
  slots: AvailabilitySlot[];
}

@Component({
  selector: "customer-appointment-modify",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./modify.component.html",
  styleUrl: "./modify.component.scss",
})
export class CustomerAppointmentModifyComponent implements OnInit {
  appointmentId = "";

  appointment: any = null;
  catalog: SalonCatalog | null = null;

  services: CatalogService[] = [];
  staffMembers: CatalogStaff[] = [];
  availableSlots: AvailabilitySlot[] = [];

  selectedServiceIds: string[] = [];
  selectedStaffId: string | null = null;

  selectedDate = "";
  selectedTime = "";
  notes = "";

  loading = true;
  loadingAvailability = false;
  saving = false;

  errorMessage = "";
  availabilityMessage = "";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private appointmentsService: AppointmentsService,
  ) {}

  ngOnInit(): void {
    this.appointmentId = this.route.snapshot.paramMap.get("id") || "";

    if (!this.appointmentId) {
      this.errorMessage = "Appointment could not be found.";

      this.loading = false;
      return;
    }

    this.loadAppointment();
  }

  private loadAppointment(): void {
    this.loading = true;
    this.errorMessage = "";

    this.appointmentsService.getAppointmentByID(this.appointmentId).subscribe({
      next: (appointment: any) => {
        this.appointment = appointment;

        const status = String(appointment?.status || "").toLowerCase();

        if (["cancelled", "completed", "no_show"].includes(status)) {
          this.errorMessage = "This appointment can no longer be modified.";

          this.loading = false;
          return;
        }

        this.selectedServiceIds = (appointment?.items || [])
          .map((item: any) => item?.service?.service_id)
          .filter(Boolean);

        this.selectedStaffId = appointment?.staff?.staff_id || null;

        this.notes = appointment?.notes || "";

        if (appointment?.start_time) {
          this.selectedDate = dayjs(appointment.start_time).format(
            "YYYY-MM-DD",
          );

          this.selectedTime = dayjs(appointment.start_time).format("h:mm A");
        }

        const salonId = appointment?.salon?.salon_id;

        if (!salonId) {
          this.errorMessage =
            "The salon for this appointment could not be loaded.";

          this.loading = false;
          return;
        }

        this.loadCatalog(salonId);
      },

      error: (error) => {
        console.error("Failed to load appointment:", error);

        this.errorMessage =
          error?.error?.detail || "Unable to load this appointment.";

        this.loading = false;
      },
    });
  }

  private loadCatalog(salonId: string): void {
    this.http
      .get<SalonCatalog>(API_ENDPOINTS.public.salonCatalog(salonId))
      .subscribe({
        next: (catalog) => {
          this.catalog = catalog;

          this.services = catalog?.services || [];

          this.staffMembers = catalog?.staff || [];

          this.loading = false;

          if (
            this.selectedStaffId &&
            this.selectedDate &&
            this.selectedServiceIds.length
          ) {
            this.loadAvailability();
          }
        },

        error: (error) => {
          console.error("Failed to load salon catalog:", error);

          this.errorMessage = "Unable to load salon services and staff.";

          this.loading = false;
        },
      });
  }

  toggleService(serviceId: string): void {
    const exists = this.selectedServiceIds.includes(serviceId);

    if (exists) {
      this.selectedServiceIds = this.selectedServiceIds.filter(
        (id) => id !== serviceId,
      );
    } else {
      this.selectedServiceIds = [...this.selectedServiceIds, serviceId];
    }

    this.selectedTime = "";
    this.availableSlots = [];

    this.tryLoadAvailability();
  }

  isServiceSelected(serviceId: string): boolean {
    return this.selectedServiceIds.includes(serviceId);
  }

  onStaffChange(): void {
    this.selectedTime = "";
    this.availableSlots = [];

    this.tryLoadAvailability();
  }

  onDateChange(): void {
    this.selectedTime = "";
    this.availableSlots = [];

    this.tryLoadAvailability();
  }

  private tryLoadAvailability(): void {
    if (
      !this.selectedStaffId ||
      !this.selectedDate ||
      !this.selectedServiceIds.length
    ) {
      return;
    }

    this.loadAvailability();
  }

  loadAvailability(): void {
    if (
      !this.appointment?.salon?.salon_id ||
      !this.selectedStaffId ||
      !this.selectedDate ||
      !this.selectedServiceIds.length
    ) {
      return;
    }

    this.loadingAvailability = true;
    this.availabilityMessage = "";

    const serviceIds = this.selectedServiceIds.join(",");

    const url = API_ENDPOINTS.public.staffAvailability(
      this.appointment.salon.salon_id,
      this.selectedStaffId,
      this.selectedDate,
      serviceIds,
    );

    this.http.get<AvailabilityResponse>(url).subscribe({
      next: (response) => {
        this.availableSlots = response?.slots || [];

        /*
         * The public availability endpoint sees the
         * existing appointment as occupied.
         *
         * Preserve the current appointment's time as
         * a valid choice when the date/staff/services
         * still represent the current booking.
         */
        this.restoreCurrentSlotIfNeeded();

        if (!this.availableSlots.some((slot) => slot.available)) {
          this.availabilityMessage =
            "No available times were found for this date.";
        }

        this.loadingAvailability = false;
      },

      error: (error) => {
        console.error("Failed to load availability:", error);

        this.availableSlots = [];

        this.availabilityMessage = "Unable to load available times.";

        this.loadingAvailability = false;
      },
    });
  }

  private restoreCurrentSlotIfNeeded(): void {
    if (!this.appointment?.start_time || !this.appointment?.staff?.staff_id) {
      return;
    }

    const originalDate = dayjs(this.appointment.start_time).format(
      "YYYY-MM-DD",
    );

    const originalTime = dayjs(this.appointment.start_time).format("h:mm A");

    const sameDate = this.selectedDate === originalDate;

    const sameStaff = this.selectedStaffId === this.appointment.staff.staff_id;

    const originalServiceIds = (this.appointment.items || [])
      .map((item: any) => item?.service?.service_id)
      .filter(Boolean)
      .sort();

    const selectedIds = [...this.selectedServiceIds].sort();

    const sameServices =
      JSON.stringify(originalServiceIds) === JSON.stringify(selectedIds);

    if (!sameDate || !sameStaff || !sameServices) {
      return;
    }

    const existingSlot = this.availableSlots.find(
      (slot) => slot.time === originalTime,
    );

    if (existingSlot) {
      existingSlot.available = true;
    } else {
      this.availableSlots = [
        {
          time: originalTime,
          available: true,
        },
        ...this.availableSlots,
      ];
    }

    if (!this.selectedTime) {
      this.selectedTime = originalTime;
    }
  }

  selectTime(slot: AvailabilitySlot): void {
    if (!slot.available) {
      return;
    }

    this.selectedTime = slot.time;
  }

  get selectedServices(): CatalogService[] {
    return this.services.filter((service) =>
      this.selectedServiceIds.includes(service.service_id),
    );
  }

  get totalDuration(): number {
    return this.selectedServices.reduce(
      (total, service) => total + Number(service.duration_minutes || 0),
      0,
    );
  }

  get estimatedTotal(): number {
    return this.selectedServices.reduce(
      (total, service) => total + Number(service.price || 0),
      0,
    );
  }

  get canSave(): boolean {
    return Boolean(
      !this.saving &&
      this.selectedServiceIds.length &&
      this.selectedStaffId &&
      this.selectedDate &&
      this.selectedTime,
    );
  }

  saveChanges(): void {
    if (!this.canSave) {
      return;
    }

    const startTime = this.buildStartTime();

    if (!startTime) {
      this.errorMessage = "Please select a valid date and time.";

      return;
    }

    this.saving = true;
    this.errorMessage = "";

    const payload: CustomerAppointmentUpdateRequest = {
      start_time: startTime,
      service_ids: [...this.selectedServiceIds],
      staff_id: this.selectedStaffId,
      notes: this.notes.trim(),
    };

    this.appointmentsService
      .updateAppointment(this.appointmentId, payload)
      .subscribe({
        next: () => {
          this.saving = false;

          this.router.navigate([
            "/admin/customer/appointment/details",
            this.appointmentId,
          ]);
        },

        error: (error) => {
          console.error("Failed to update appointment:", error);

          this.errorMessage = this.getApiErrorMessage(error);

          this.saving = false;
        },
      });
  }

  private buildStartTime(): string | null {
    if (!this.selectedDate || !this.selectedTime) {
      return null;
    }

    const parsed = dayjs(
      `${this.selectedDate} ${this.selectedTime}`,
      "YYYY-MM-DD h:mm A",
    );

    if (!parsed.isValid()) {
      return null;
    }

    return parsed.toISOString();
  }

  private getApiErrorMessage(error: any): string {
    const response = error?.error;

    if (typeof response?.detail === "string") {
      return response.detail;
    }

    if (typeof response?.start_time === "string") {
      return response.start_time;
    }

    if (Array.isArray(response?.start_time)) {
      return response.start_time[0];
    }

    if (typeof response?.service_ids === "string") {
      return response.service_ids;
    }

    if (Array.isArray(response?.service_ids)) {
      return response.service_ids[0];
    }

    if (typeof response?.staff_id === "string") {
      return response.staff_id;
    }

    if (Array.isArray(response?.staff_id)) {
      return response.staff_id[0];
    }

    if (Array.isArray(response?.non_field_errors)) {
      return response.non_field_errors[0];
    }

    return "Unable to update your appointment. Please try again.";
  }

  cancel(): void {
    this.router.navigate([
      "/admin/customer/appointment/details",
      this.appointmentId,
    ]);
  }

  formatMoney(value: string | number): string {
    return Number(value || 0).toLocaleString("en-CA", {
      style: "currency",
      currency: "CAD",
    });
  }

  trackService(_index: number, service: CatalogService): string {
    return service.service_id;
  }

  trackStaff(_index: number, staff: CatalogStaff): string {
    return staff.staff_id;
  }

  trackSlot(_index: number, slot: AvailabilitySlot): string {
    return slot.time;
  }
}
